import { Button, Grid2 } from '@mui/material'
import { useState } from 'react'
import { Formik, Field, Form } from "formik";
import * as yup from "yup";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { AutoCompleteField } from '../../shared/components/formfields/AutoCompleteField';
import api from '../../assets/nodeApi/axiosInstance';
import { useToaster } from '../../shared/utils/ToasterProvider';
import useAuthStore from '../../store/useAuthStore';
import { useAllZoneList } from '../../assets/api/useMarketingExecutive';
import { IdAutoCompleteField } from '../../shared/components/formfields/IdAutoCompleteField';
import { useMarketingManagerList } from '../../assets/api/useMarketingManager';
import { useApartmentListForCourse, useGetAllUserDetailList, useRoleList } from '../../assets/api/useAdminApi';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { PhoneInputFormField } from '../../shared/components/formfields/PhoneInputFormField';
import { Grid_Gap } from '../../shared/constants';
import { LocationSelectorField } from '../../shared/components/LocationSelectorField';
import { useAdminZoneStore } from '../../store/useAdminZoneStore';
import CustomDivider from '../../shared/utils/CustomDivider';

const AddUserForm = ({ onClose, RoleOption, role = "", onSuccess }) => {
    const [selectedMainRole, setSeletedMainRole] = useState("");
    const [zonesFromChild, setZonesFromChild] = useState("")
    const { data: roleList, isLoading } = useRoleList();
    const excludedRoles = ["student", "trainer"];
    const roleRows =
        roleList?.roles
            ?.filter(
                (role) =>
                    !excludedRoles.includes(role.toLowerCase()) &&
                    role.toLowerCase() !== selectedMainRole?.toLowerCase()
            )
            ?.map((item, index) => ({
                id: index + 1,
                value: item,
                label: item,
            })) || [];

    const { showToaster } = useToaster();
    const queryClient = useQueryClient();
    const { data: allUserDetailList } = useGetAllUserDetailList()
    const { token, user } = useAuthStore()
    const [selectedRole, SetSelectedRole] = useState(null)
    const { data: ZoneList, isLoading: isLoadingZone } = useAllZoneList()
    const { data: apartmentList, isLoading: isLoadingApartmentList } = useApartmentListForCourse();
    const ApartmentOptions = apartmentList?.data.map((item) => ({
        id: item?.id,
        label: item?.apartment_name,
        value: item?.apartment_name,
    })) || [];
    const ZoneOption = ZoneList?.data?.map((item) => ({
        id: item?.id,
        value: item?.zone_name,
        label: item?.zone_name
    })) || [];
    const getRoleOptionsByMainRole = (mainRole) => {
        switch (mainRole) {
            case "Marketing Manager":
                return ["Admin", "Zone Manager"];
            case "Marketing Executive":
                return ["Marketing Manager", "Admin",];
            case "coordinator":
                return ["Admin", "Zone Manager", "Zone Head"];
            case "Zone Head":
            case "Zone Manager":
                return ["Admin"];
            default:
                return ["Admin"];
        }
    };
    const RoleOption1 = (getRoleOptionsByMainRole(selectedMainRole) || []).map(
        (item, index) => ({
            id: index + 1,
            value: item,
            label: item,
        })
    );
    const { data: userList, isLoading: isReportingToLoading } = useMarketingManagerList({ role: user?.role === "Admin" ? selectedRole : user?.role })
    const UserListOption = userList?.map((item) => ({
        id: item?.id,
        value: `${item?.firstname || ""} ${item?.lastname || ""}`.trim(),
        label: `${item?.firstname || ""} ${item?.lastname || ""}`.trim(),
    })) || [];

    const baseSchema = {
        firstname: yup.string().required("First Name is required"),
        lastname: yup.string().required("Last Name is required"),
        username: yup
            .string()
            .required("User Name is required")
            .test("username-taken", "Username already taken", function (value) {
                return !allUserDetailList?.data?.some(user => user.username === value);
            }),
        email: yup
            .string()
            .email("Invalid email format")
            .required("Email is required")
            .test("email-taken", "Email already taken", function (value) {
                return !allUserDetailList?.data?.some(user => user.email === value);
            }),
        mobile: yup
            .string()
            .required("Mobile Number is required")
            .test("valid-phone", "Phone number is not valid", value =>
                value && isValidPhoneNumber(value, "IN")
            )
            .test("mobile-taken", "Mobile number already taken", function (value) {
                return !allUserDetailList?.data?.some(user => user.mobile === value);
            }),
        password: yup
            .string()
            .trim()
            .min(8, "Password must be at least 8 characters")
            .required("Password is required"),
        confirm_password: yup
            .string()
            .trim()
            .oneOf([yup.ref("password"), null], "Passwords must match")
            .required("Confirm Password is required"),
        country_name: yup.string().required("Country Name is required"),
        state: yup.string().required("State Name is required"),
        city: yup.string().required("City Name is required"),
        zip: yup
            .string()
            .required("Pincode is required")
            .matches(/^\d{6}$/, "Pincode must be exactly 6 digits"),
    };

    const studentSchema = {
        ...baseSchema,
        apartment_name: yup.string().required("Apartment is required"),
        age: yup.string().required("Age is required"),
        child_grade: yup.string().required("Student's Grade is required"),
        child_school: yup.string().required("School Name is required"),
        address: yup.string().required("Address is required")
    };

    const otherUserSchema = {
        ...baseSchema,
        role: yup.string().required("role is required"),
        zone_id: yup.string().when("role", {
            is: (role) =>
                ["Marketing Executive", "Zonal Coordinator", "Parent Counsellor"].includes(role),
            then: (schema) => schema.notRequired().nullable(),
            otherwise: (schema) => schema.required("Zone is required"),
        }),
        reporting_to: yup.string().required("Reporting To Persone is required"),
    };

    let validationSchema;
    if (role === "student") {
        validationSchema = yup.object().shape(studentSchema);
    } else {
        validationSchema = yup.object().shape(otherUserSchema);
    }
    const route = role === "student" ? "collab-register" : "user-register"
    const baseValues = {
        firstname: "",
        lastname: "",
        email: "",
        mobile: "",
        password: "",
        confirm_password: "",
        username: "",
        country_name: "",
        state: "",
        city: "",
        zip: "",
        address: ""
    };
    const studentValues = {
        ...baseValues,
        apartment_name: "",
        age: "",
        child_grade: "",
        child_school: "",
    };
    const otheruserValues = {
        ...baseValues,
        role: "",
        zone_id: "",
        reporting_to: ""
    };
    let initialValues;
    if (role === "student") {
        initialValues = studentValues;
    } else {
        initialValues = otheruserValues;
    }
    const mutation = useMutation({
        mutationFn: async (formData) => {
            const response = await api.post(route, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        },
    });
    const selectedZones = useAdminZoneStore((s) => s.selectedZones);
    const clearZones = useAdminZoneStore((s) => s.clearZones);

    const handleSubmit = async (values, { resetForm }) => {
        const filteredValues = {
            ...Object.fromEntries(
                Object.entries(values).filter(([key]) => key !== "confirm_password")
            ),
            zone_id: selectedZones.map(z => z.id),
            status: true,
        };

        mutation.mutate(filteredValues, {
            onSuccess: () => {
                showToaster('Success', 'User added successfully', 'success');
                queryClient.invalidateQueries({ queryKey: ['coordinator-user'] });
                queryClient.invalidateQueries({ queryKey: ['unassigned-coordinator'] });
                // ✅ Invalidate the all users list so the table refreshes with new user
                queryClient.invalidateQueries({ queryKey: ['get-allusers-list'] });
                clearZones();
                resetForm();
                onClose?.();
                // ✅ Notify parent that a user was successfully added
                onSuccess?.();
            },
            onError: (error) => {
                if (error?.response?.status === 400) {
                    showToaster(error.response.data.message, 'Error', 'error');
                } else {
                    showToaster('Error Caught While submitting the form', 'Error', 'error');
                }
            }
        });
    };

    const renderStudentFields = () => {
        if (role !== "student") return null;

        return (
            <>
                <Grid2 children size={{ xs: 12, sm: 6 }}>
                    <Field
                        name="apartment_name"
                        component={AutoCompleteField}
                        options={ApartmentOptions}
                        label="Apartment"
                        isRequired={true}
                        isLoading={isLoadingApartmentList}
                    />
                </Grid2>
                <Grid2 children size={{ xs: 12, sm: 6 }}>
                    <Field
                        name="age"
                        component={TextFormField}
                        label="Student's Age"
                        isRequired={true}
                        type={"number"}
                    />
                </Grid2>
                <Grid2 children size={{ xs: 12, sm: 6 }}>
                    <Field
                        name="child_grade"
                        component={TextFormField}
                        label="Student's Grade"
                        isRequired={true}
                    />
                </Grid2>
                <Grid2 children size={{ xs: 12, sm: 6 }}>
                    <Field
                        name="child_school"
                        component={TextFormField}
                        label="Student's School"
                        isRequired={true}
                    />
                </Grid2>
                <Grid2 children size={{ xs: 12, sm: 6 }}>
                    <Field
                        name="address"
                        component={TextFormField}
                        label="Address"
                        isRequired={true}
                    />
                </Grid2>
            </>
        );
    };

    const getFirstNameLabel = () => role === "student" ? "Parent's Name" : "First Name";
    const getLastNameLabel = () => role === "student" ? "Student's Name" : "Last Name";

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validateOnBlur={true}
        >


            {({ resetForm }) => (
                <Form>
                    <Grid2 container spacing={3} pb={2}>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="role"
                                component={AutoCompleteField}
                                options={RoleOption ??
                                    [
                                        { id: 1, label: "Coordinator", value: "coordinator" },
                                        { id: 2, label: 'Marketing Executive', value: "Marketing Executive" },
                                        { id: 3, label: 'Marketing Manager', value: "Marketing Manager" },
                                        { id: 4, label: 'Zone Manager', value: "Zone Manager" },
                                        { id: 5, label: 'Zone Head', value: "Zone Head" },
                                        { id: 6, label: 'Zonal Coordinator', value: "Zonal Coordinator" },
                                        { id: 7, label: 'Parent Counsellor', value: "Parent Counsellor" },
                                        { id: 8, label: 'Human Resources', value: "HR" },
                                        { id: 9, label: 'CTO', value: "CTO" },
                                        { id: 10, label: 'COO', value: "COO" },
                                        { id: 11, label: 'CEO', value: "CEO" },
                                        { id: 12, label: 'LAM', value: "LAM" },
                                        { id: 13, label: 'SME', value: "SME" },
                                        { id: 14, label: 'CMO', value: "CMO" },
                                        { id: 15, label: 'CFO', value: "CFO" },
                                        { id: 16, label: 'CAO', value: "CAO" },
                                        { id: 17, label: 'CHRO', value: "CHRO" },
                                    ]}
                                label="Select Role"
                                isRequired={true}
                                onValueChange={(val) => { setSeletedMainRole(val?.value) }}
                                onClear={() => setSeletedMainRole(null)}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="firstname"
                                component={TextFormField}
                                label={getFirstNameLabel()}
                                isRequired={true}
                                type={"name"}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="lastname"
                                component={TextFormField}
                                label={getLastNameLabel()}
                                isRequired={true}
                                type={"name"}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="email"
                                component={TextFormField}
                                label="E-mail"
                                isRequired={true}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="username"
                                component={TextFormField}
                                label="User Name"
                                isRequired={true}
                                autoComplete="off"
                            />
                        </Grid2>
                        {renderStudentFields()}
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="mobile"
                                component={PhoneInputFormField}
                                label="Phone Number"
                                isRequired={true}
                            />
                        </Grid2>
                        {role !== "student" && (
                            <>
                                {user?.role === "Admin" && (
                                    <Grid2 children size={{ xs: 12, sm: 6 }}>
                                        <Field
                                            name="roletoassign"
                                            component={AutoCompleteField}
                                            options={roleRows}
                                            label="Select Reporting Role"
                                            isRequired={true}
                                            onValueChange={(role) => SetSelectedRole(role?.value)}
                                            onClear={() => SetSelectedRole(null)}
                                        />
                                    </Grid2>
                                )}
                                <Grid2 children size={{ xs: 12, sm: 6 }}>
                                    <Field
                                        name="reporting_to"
                                        component={IdAutoCompleteField}
                                        options={UserListOption}
                                        label="Person to Report"
                                        isRequired={true}
                                        isLoading={isReportingToLoading}
                                    />
                                </Grid2>
                            </>
                        )}
                        <LocationSelectorField
                            countryField="country_name"
                            stateField="state"
                            cityField="city"
                            isStoredId={false}
                            zipField="zip"
                            zoneField="zone_id"
                            isProfile={false}
                            Input_Width={{ xs: 12, sm: 6 }}
                            isZone={role !== "student"}
                            selectedMainRole={selectedMainRole}
                            {...(user?.role === "Marketing Manager"
                                ? {
                                    passedCityId: user?.city_id,
                                    passedCountryId: user?.country_id,
                                    passedStateId: user?.state_id,
                                }
                                : {}
                            )}
                        />
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="password"
                                component={TextFormField}
                                label="Password"
                                isRequired={true}
                                type={"password"}
                                autoComplete="new-password"
                                inputProps={{ autoComplete: "new-password" }}
                            />
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 6 }}>
                            <Field
                                name="confirm_password"
                                component={TextFormField}
                                label="Confirm Password"
                                isRequired={true}
                                type={"password"}
                                autoComplete="new-password"
                                inputProps={{ autoComplete: "new-password" }}
                            />
                        </Grid2>
                    </Grid2>
                    <CustomDivider />
                    <Grid2 container spacing={Grid_Gap} sx={{ justifyContent: "end" }}>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Button variant='outlined' fullWidth onClick={resetForm}>Cancel</Button>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                            <Button variant='contained' fullWidth type="submit">Add</Button>
                        </Grid2>
                    </Grid2>
                </Form>
            )}
        </Formik>
    )
}

export default AddUserForm